import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import NewDocumentGroupButton from '@UI/Pages/Administration/DocumentGroups/DocumentGroupList/NewDocumentGroupButton';
import WorkflowRow from '@UI/Pages/Administration/DocumentGroups/DocumentGroupList/WorkflowRow';
import HintText from '@Guidelines/Component/HintText';
import ScreenHeading from '@UI/Pages/Administration/DocumentGroups/Shared/ScreenHeading';
import describeStages from '@UI/Pages/Administration/DocumentGroups/DocumentGroupList/Helpers/describeStages';
import useOrganizationWorkflows from '@UI/Pages/Administration/DocumentGroups/Shared/Hooks/useOrganizationWorkflows';
import OrganizationPending from '@UI/Pages/Administration/DocumentGroups/Shared/OrganizationPending';
import formatLongDate from '@UI/Helpers/formatLongDate';
import { css } from '@emotion/react';
import { Layers } from 'lucide-react';
import type * as React from 'react';
import { Skeleton } from '@verclam/ui-kit';

const screenStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.625rem;
`;

const sectionStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`;

const sectionTitleStyle = css`
	${theme.typography.eyebrow}
	margin: 0;
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

const sectionHeaderStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.375rem;
`;

const skeletonStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

/**
 * The current organization's document groups, then its automatic transcriptions, in the order the
 * server gives. Owns the workflows query and the screen heading, so the loading and failure states
 * replace the whole screen body but never its title.
 */
const DocumentGroupListContent: React.FC = () => {
	const { workflows, error, refetch, organizationId, hasNoOrganization } = useOrganizationWorkflows();

	const heading = (
		<ScreenHeading
			title="Groupes de documents"
			description="Ce que les professionnels peuvent préparer dans un dossier, et les transcriptions faites automatiquement."
			actions={<NewDocumentGroupButton />}
		/>
	);

	if (organizationId === null) {
		return (
			<div css={screenStyle}>
				{heading}
				<OrganizationPending hasNoOrganization={hasNoOrganization} />
			</div>
		);
	}

	if (workflows === null && error === null) {
		return (
			<div css={screenStyle}>
				{heading}
				<div css={skeletonStyle}>
					<Skeleton height="6rem" borderRadius={theme.radii.large} animation="wave" />
					<Skeleton height="6rem" borderRadius={theme.radii.large} animation="wave" />
					<Skeleton height="6rem" borderRadius={theme.radii.large} animation="wave" />
				</div>
			</div>
		);
	}

	if (workflows === null) {
		return (
			<div css={screenStyle}>
				{heading}
				<ErrorState title="Impossible de charger les groupes de documents." onRetry={refetch} />
			</div>
		);
	}

	const { documentGroups, automaticWorkflows } = workflows;

	return (
		<div css={screenStyle}>
			{heading}
			<section css={sectionStyle} aria-labelledby="document-groups-title">
				<h2 id="document-groups-title" css={sectionTitleStyle}>
					Groupes
				</h2>
				<ConditionalLoader
					data={documentGroups.length > 0}
					fallback={
						<EmptyState
							title="Aucun groupe : créez le premier."
							icon={<Layers size={28} />}
							action={<NewDocumentGroupButton />}
						/>
					}
				>
					{documentGroups.map((workflow) => (
						<WorkflowRow
							key={workflow.key}
							to={`/administration/document-groups/${workflow.key}`}
							ariaLabel={`Configurer le groupe « ${workflow.label} »`}
							name={workflow.label}
							description={workflow.description}
							summary={describeStages(workflow)}
							meta={
								workflow.isActive
									? `version ${workflow.version}, publiée le ${formatLongDate(workflow.publishedAt)}`
									: `version ${workflow.version}, archivée`
							}
							isActive={workflow.isActive}
							inactiveLabel="Archivé"
						/>
					))}
				</ConditionalLoader>
			</section>
			<ConditionalLoader data={automaticWorkflows.length > 0}>
				<section css={sectionStyle} aria-labelledby="automatic-transcription-title">
					<div css={sectionHeaderStyle}>
						<h2 id="automatic-transcription-title" css={sectionTitleStyle}>
							Transcription automatique
						</h2>
						<HintText>Faite à chaque entretien ou note ajouté, avant toute génération.</HintText>
					</div>
					{automaticWorkflows.map((workflow) => (
						<WorkflowRow
							key={workflow.key}
							to={`/administration/document-groups/${workflow.key}`}
							ariaLabel={`Régler « ${workflow.label} »`}
							name={workflow.label}
							description={workflow.description}
							summary={`${workflow.steps.length} étape${workflow.steps.length > 1 ? 's' : ''} · non modifiables`}
							isActive={workflow.isActive}
							inactiveLabel="Désactivé"
						/>
					))}
				</section>
			</ConditionalLoader>
		</div>
	);
};

export default DocumentGroupListContent;
